import axiosInstance from './axiosInstance'

/**
 * Helper to split a large array into chunks and send them in authorized batches
 * to avoid HTTP timeouts (e.g., 30s limit for 66k+ nodes).
 * 
 * @param {string} url - API Endpoint
 * @param {Object[]} rows - Data payload
 * @param {number} [batchSize=2000] - Nodes per request
 */
async function batchImport(url, rows, batchSize = 1000) {
  let totalImported = 0;
  for (let i = 0; i < rows.length; i += batchSize) {
    const chunk = rows.slice(i, i + batchSize);
    const batchNum = Math.floor(i / batchSize) + 1;
    const totalBatches = Math.ceil(rows.length / batchSize);
    
    console.log(`🚀 [Intelligence Hub] Batch ${batchNum}/${totalBatches} | Processing records ${i + 1} to ${Math.min(i + batchSize, rows.length)}...`);
    
    const response = await axiosInstance.post(url, chunk);
    totalImported += (response.data.imported || 0);

    // Throttle: 250ms delay between batches to bypass Gateway/Infrastructure rate limits
    if (i + batchSize < rows.length) {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
  }
  return { success: true, imported: totalImported };
}

/**
 * Import Demand records parsed from CSV into MongoDB.
 */
export async function importDemand(rows) {
  try {
    return await batchImport('/api/datasets/demand/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Demand import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Import Supply records parsed from XLSX into MongoDB.
 */
export async function importSupply(rows) {
  try {
    return await batchImport('/api/datasets/supply/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Supply import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing demand records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getDemandRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/demand')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch demand records'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing supply records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getSupplyRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/supply')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch supply records'
    throw new Error(errorMessage)
  }
}

/**
 * Upload a raw MSA Excel file for background ingestion on the server.
 * This is the preferred method for large datasets (>10k nodes).
 * 
 * @param {File} file - Raw .xlsx or .csv file
 */
export async function uploadMsaFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post('/api/datasets/msa/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'MSA file upload failed';
    throw new Error(errorMessage);
  }
}

export async function uploadDemandFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/datasets/demand/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data || error.message || 'Demand file upload failed');
  }
}

export async function uploadSupplyFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/datasets/supply/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data || error.message || 'Supply file upload failed');
  }
}

export async function uploadBosFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/datasets/bos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data || error.message || 'BOS file upload failed');
  }
}

export async function uploadNationalFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/datasets/national/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data || error.message || 'National file upload failed');
  }
}

export async function uploadStateFile(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/datasets/state/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data || error.message || 'State file upload failed');
  }
}

/**
 * Import MSA records parsed from XLSX into MongoDB.
 */
export async function importMsaOccupations(rows) {
  try {
    return await batchImport('/api/datasets/msa/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'MSA import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing MSA regional records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getMsaRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/msa')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch MSA records'
    throw new Error(errorMessage)
  }
}

/**
 * Import BOS national OEWS records parsed from XLSX into MongoDB.
 */
export async function importBosOccupations(rows) {
  try {
    return await batchImport('/api/datasets/bos/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'BOS import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing BOS national OEWS records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getBosRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/bos')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch BOS records'
    throw new Error(errorMessage)
  }
}

/**
 * Import National NAICS records parsed from XLSX into MongoDB.
 */
export async function importNationalOccupations(rows) {
  try {
    return await batchImport('/api/datasets/national/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'National NAICS import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing National NAICS records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getNationalRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/national')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch National records'
    throw new Error(errorMessage)
  }
}

/**
 * Import State OEWS records parsed from XLSX into MongoDB.
 */
export async function importStateOccupations(rows) {
  try {
    return await batchImport('/api/datasets/state/import', rows);
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'State OEWS import failed'
    throw new Error(errorMessage)
  }
}

/**
 * Fetch pre-aggregated analytics for a specific dataset type.
 * @param {string} type - bos, national, state, or msa
 * @param {string} [year] - optional session year filter
 * @returns {Promise<Object>} DatasetAnalyticsDTO
 */
export async function getDatasetAnalytics(type, year) {
  try {
    const url = `/api/datasets/${type}/analytics${year ? `?year=${year}` : ''}`
    const response = await axiosInstance.get(url)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || `Failed to fetch ${type} analytics`
    throw new Error(errorMessage)
  }
}

/**
 * Fetch unabridged list of occupations explicitly by area.
 * @param {string} type - bos, national, state, or msa
 * @param {string} areaName - The specific geographic area title to search
 * @param {string} [year] - optional session year filter
 * @returns {Promise<Object[]>}
 */
export async function getOccupationsByArea(type, areaName, year) {
  try {
    const url = `/api/datasets/${type}/area/${encodeURIComponent(areaName)}${year ? `?year=${year}` : ''}`
    const response = await axiosInstance.get(url)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || `Failed to fetch occupations for area ${areaName}`
    throw new Error(errorMessage)
  }
}

/**
 * Fetch all existing State OEWS records from the database.
 * @returns {Promise<Object[]>}
 */
export async function getStateRecords() {
  try {
    const response = await axiosInstance.get('/api/datasets/state')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data || error.message || 'Failed to fetch State records'
    throw new Error(errorMessage)
  }
}
